import { makeStyles } from '@material-ui/core';
import * as React from 'react';
import { useEffect } from 'react';
import { AutocompleteInput, Filter, ReferenceInput, TextInput, useListContext, useTranslate } from 'react-admin';
import { GROUP_METADATA_AVATAR_TYPES, METADATA_AVATAR_TYPES } from '../../../providers/resources';
import { getNameWithoutPrefixFromJson as getGroupNameWithoutPrefixFromJson } from '../../../utils/resources/GroupMetadataAvatarType';
import { getNameWithoutPrefixFromJson } from '../../../utils/resources/MetadataAvatarType';
import DepartmentFilterInput from '../../avatarAnc/filters/DepartmentFilterInput';
import StatusFilter from '../../avatarAnc/filters/StatusFilter';
import ThresholdNameSelectInput from '../../avatarAnc/filters/ThresholdNameSelectInput1';
import PeriodInputV2 from './PeriodInputV2';

/**
 * Date filters only cover the view's real timestamp columns. reception_date, reserve_date and
 * form2_date_installation_or_invoice are free-form text in the view, so they are searched as text.
 */
const DATE_SOURCES = [
    'productInstallationDate',
    'poseDate',
    'readyToDeliverDate',
    'reserveLiftDate',
    'productLastMaintenanceDate',
    'productLastEmptyingDate',
];

const styles = () => ({
    form: {
        marginLeft: 0,
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'flex-end' as const,
        gap: 16,
    },
});

const useStyles = makeStyles(styles);

const ListFilterV2 = (props: any) => {
    const t = useTranslate();
    const classes = useStyles();
    const { setFilters, displayedFilters, filterValues } = useListContext();

    // react-admin keeps the values of a filter that has been hidden again; drop them so the
    // list does not stay narrowed by a filter the user can no longer see (same as the v1 view).
    useEffect(() => {
        let next = { ...filterValues };
        let changed = false;

        DATE_SOURCES.forEach((source) => {
            if (!(source in displayedFilters)) {
                [`${source}__after`, `${source}__before`].forEach((key) => {
                    if (key in next) {
                        delete next[key];
                        changed = true;
                    }
                });
            }
        });

        if (changed) {
            setFilters(next, displayedFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedFilters]);

    return (
        <Filter {...props} classes={{ form: classes.form }}>
            <StatusFilter
                className='firstInput'
                alwaysOn={true}
                source='productCurrentStatusName'
                label={t('app.ancs.nodeStateName')}
            />

            <DepartmentFilterInput source='department' alwaysOn={true} />

            <TextInput
                size={'small'}
                variant={'standard'}
                label={t('app.ancs.serialNumber')}
                source='productSerialNumber'
                InputLabelProps={{ shrink: true }}
            />

            <TextInput
                size={'small'}
                variant={'standard'}
                label={t('app.ancsV2.filters.clientFullName')}
                source='clientFullName'
                InputLabelProps={{ shrink: true }}
            />

            <TextInput
                size={'small'}
                variant={'standard'}
                label={t('app.ancsV2.filters.installerCompany')}
                source='installerCompany'
                InputLabelProps={{ shrink: true }}
            />

            <TextInput
                size={'small'}
                variant={'standard'}
                label={t('app.ancsV2.filters.distributorCompany')}
                source='distributorCompany'
                InputLabelProps={{ shrink: true }}
            />

            <TextInput
                size={'small'}
                variant={'standard'}
                label={t('app.ancsV2.filters.installationCity')}
                source='installationCity'
                InputLabelProps={{ shrink: true }}
            />

            <PeriodInputV2 source='poseDate' label={t('app.ancsV2.columns.poseDate')} />
            <PeriodInputV2 source='readyToDeliverDate' label={t('app.ancsV2.columns.readyToDeliverDate')} />
            <PeriodInputV2 source='reserveLiftDate' label={t('app.ancsV2.columns.reserveLiftDate')} />
            <PeriodInputV2 source='productInstallationDate' label={t('app.ancs.installingDate')} />
            <PeriodInputV2
                source='productLastMaintenanceDate'
                label={t('app.ancs.sludgeMaintenanceThresholdFilterLastDate')}
            />
            <PeriodInputV2
                source='productLastEmptyingDate'
                label={t('app.ancs.sludgeEmptyingThresholdFilterLastDate')}
            />

            <ThresholdNameSelectInput
                source={'productLastMaintenanceThresholdName'}
                label={t('app.traceabilityNotesThreshold.maintenance.name')}
            />

            <ThresholdNameSelectInput
                source={'productLastEmptyingThresholdName'}
                label={t('app.traceabilityNotesThreshold.emptying.name')}
            />

            <ReferenceInput
                size={'medium'}
                variant={'standard'}
                label={t('app.ancs.name')}
                source='metadataAvatarTypeId'
                reference={METADATA_AVATAR_TYPES}
                filterToQuery={(searchText) => ({ name: searchText })}
            >
                <AutocompleteInput optionText={getNameWithoutPrefixFromJson} />
            </ReferenceInput>

            <ReferenceInput
                size={'medium'}
                variant={'standard'}
                label={t('app.ancs.productTypology')}
                source='groupMetadataAvatarTypeId'
                reference={GROUP_METADATA_AVATAR_TYPES}
                filterToQuery={(searchText) => ({ name: searchText })}
            >
                <AutocompleteInput optionText={getGroupNameWithoutPrefixFromJson} />
            </ReferenceInput>
        </Filter>
    );
};

export default ListFilterV2;
